import React, { useState, useMemo, useCallback } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { 
  Box, 
  IconButton, 
  Tooltip
} from '@mui/material';
import {
  Edit as EditIcon
} from '@mui/icons-material';
import TrashIcon from '../../assets/Trash.svg';
import { DataTable, AlertDialog } from '../Common';

const RoleList = ({ 
  roles, 
  onDeleteRole, 
  onEditRole,
  searchTerm: externalSearchTerm, 
  onSearchChange: externalOnSearchChange,
  showSearchOnly = false 
}) => {
  console.log('RoleList received roles:', roles);
  console.log('RoleList roles length:', roles?.length);
  const { mode } = useThemeMode();
  const { hasEditPermission, hasDeletePermission } = usePermissions();
  const [internalSearchTerm, setInternalSearchTerm] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    roleId: null,
    roleName: ''
  });

  // Use external search term if provided, otherwise use internal state
  const searchTerm = externalSearchTerm !== undefined ? externalSearchTerm : internalSearchTerm;
  const setSearchTerm = externalOnSearchChange || setInternalSearchTerm;

  // Search function for roles
  const searchRoles = (roles, searchTerm) => {
    if (!searchTerm) return roles;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return roles.filter(role => 
      role.name.toLowerCase().includes(lowerSearchTerm) ||
      (role.role_type && role.role_type.toLowerCase().includes(lowerSearchTerm)) ||
      (role.description && role.description.toLowerCase().includes(lowerSearchTerm)) ||
      (role.employee_count && role.employee_count.toString().includes(lowerSearchTerm))
    );
  };

  // Delete handlers
  const handleDeleteClick = useCallback((role) => {
    setDeleteDialog({
      isOpen: true,
      roleId: role.id,
      roleName: role.name
    });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDialog.roleId) return;

    try {
      // Defer API call to parent to avoid double-calling
      if (onDeleteRole) {
        await onDeleteRole(deleteDialog.roleId);
      }
    } catch (error) {
      console.error('Error deleting role (handled by parent):', error);
    } finally {
      setDeleteDialog({ isOpen: false, roleId: null, roleName: '' });
    }
  }, [deleteDialog.roleId, onDeleteRole]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialog({ isOpen: false, roleId: null, roleName: '' });
  }, []);

  // Memoize columns to prevent recreation on every render
  const columns = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Role Name',
      size: 150,
      minSize: 120,
      maxSize: 200,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          lineHeight: '1.4',
          fontWeight: '500'
        }}>
          {row.original.name}
        </span>
      ),
    },
    {
      accessorKey: 'role_type',
      header: 'Role Type',
      size: 120,
      minSize: 100,
      maxSize: 150,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#666666',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          lineHeight: '1.4',
          textTransform: 'capitalize'
        }}>
          {row.original.role_type}
        </span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Role Description',
      size: 200,
      minSize: 150,
      maxSize: 300,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#666666',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif',
          wordWrap: 'break-word',
          whiteSpace: 'normal',
          lineHeight: '1.4'
        }}>
          {row.original.description || 'N/A'}
        </span>
      ),
    },
    {
      accessorKey: 'employee_count',
      header: 'Total Employees',
      size: 120,
      minSize: 100,
      maxSize: 150,
      Cell: ({ row }) => (
        <span style={{ 
          color: mode === 'dark' ? '#FFFFFF' : '#333333',
          fontSize: '14px',
          fontFamily: 'Urbanist, Arial, sans-serif',
          fontWeight: '500'
        }}>
          {row.original.employee_count || 0}
        </span>
      ),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      size: 80,
      minSize: 70,
      maxSize: 90,
      enableSorting: false,
      Cell: ({ row }) => {
        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {hasEditPermission('employee') && (
              <Tooltip title="Edit">
                <IconButton 
                  size="small" 
                  sx={{ color: '#26C3C0' }}
                  onClick={() => onEditRole && onEditRole(row.original)}
                >
                  <EditIcon fontSize="small" style={{ width: '20px', height: '20px', color: '#26C3C0' }} />
                </IconButton>
              </Tooltip>
            )}
            {hasDeletePermission('employee') && (
              <Tooltip title="Delete">
                <IconButton 
                  size="small" 
                  sx={{ color: '#f44336' }}
                  onClick={() => handleDeleteClick(row.original)}
                >
                  <img 
                    src={TrashIcon} 
                    alt="Delete" 
                    style={{ 
                      width: '20px', 
                      height: '20px'
                    }} 
                  />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ], [mode, onEditRole, handleDeleteClick, hasEditPermission, hasDeletePermission]);

  return (
    <div>
      {console.log('DataTable data:', roles)}
      {console.log('DataTable columns:', columns)}
      <DataTable
        data={roles}
        columns={columns}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showSearchOnly={showSearchOnly}
        searchPlaceholder="Search roles..."
        searchFunction={searchRoles}
        emptyMessage="No roles found"
        enableSorting={true}
        shadow={false}
        borderRadius="0"
        customStyles={{
          boxShadow: 'none',
          borderRadius: '0',
          '& .MuiTableRow-root': {
            borderRadius: '0 !important',
          },
          '& .MuiTableCell-root': {
            borderRadius: '0 !important',
          },
          '& .MuiTableRow-root:last-child': {
            borderRadius: '0 !important',
            '& .MuiTableCell-root': {
              borderRadius: '0 !important',
            }
          }
        }}
      />
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        type="delete"
        title="Delete Role"
        message="Are you sure you want to delete"
        itemName={deleteDialog.roleName}
        confirmText="Delete"
        icon={TrashIcon}
      />
    </div>
  );
};

export default RoleList;